import type { ApolloLink, NormalizedCacheObject } from '@apollo/client';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { Headers, fetch } from 'cross-fetch';

import { config } from 'src/config';
import { isServer } from 'src/shared/helpers/environment';

import type { RefreshLinkOptions } from './link';
import { auth, createRefreshLink, queueLink } from './link';

interface ClientOptions extends Pick<RefreshLinkOptions, 'onUnauthenticated'> {
  links?: ApolloLink[];
  state?: string;
  terminatingLink?: ApolloLink;
  uri: string;
}

if (isServer()) {
  global.Headers = Headers;
}

// FIXME: use singleton pattern to avoid this mutable export?
// eslint-disable-next-line import/no-mutable-exports
let client: ApolloClient<NormalizedCacheObject>;

const createClient = ({
  links = [],
  onUnauthenticated,
  state,
  terminatingLink,
  uri,
}: ClientOptions) => {
  const refresh = createRefreshLink({ onUnauthenticated });
  const terminator =
    terminatingLink ??
    createHttpLink({
      uri,
      credentials: 'include',
      fetch,
    });

  const link = isServer()
    ? from([...links, terminator])
    : from([refresh, queueLink, ...links, auth, terminator]);

  const cache = new InMemoryCache();
  try {
    cache.restore(JSON.parse(state ?? '{}') as NormalizedCacheObject);
  } catch (e) {
    // intentional noop
  }

  client = new ApolloClient({
    cache,
    link,
    name: config.APPLICATION_NAME,
    ssrMode: isServer(),
    version: config.VERSION,
  });

  return client;
};

export { client, createClient };
